import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Form, Formik } from 'formik';
import Yup from 'utils/validation/yup-extensions';
import FormControlCustomized from '../layouts/FormControlCustomized';
import RequiredIndicator from 'pages/ApplicationRequest/components/containers/RequiredIndicator';
import { submitJobCategoryMappingForm } from '../_api';
import { JobCategoryMappingDTO } from '../_classes';
import { BEM_NAME_ELEMENTS, JOB_CATEGORY_MAPPING_KEYS } from '../_const';
import JobCategoryAsyncAutocomplete from '../components/JobCategoryAsyncAutocomplete';
import HRJobTitleAsyncAutocomplete from '../components/HRJobTitleAsyncAutocomplete';
import HRDepartmentAsyncAutocomplete from '../components/HRDepartmentAsyncAutocomplete';

/**
 * Create or update a Job Category Mapping
 * @param {object} jobCategoryMapping job category mapping details (empty if new)
 * @param {boolean} isNew flag for indicating new job category mapping
 * @param {object} maintenanceInformation currently logged in user information
 * @returns job category mapping creation/editing form
 */
const FormView = ({ jobCategoryMapping, isNew, maintenanceInformation }) => {
	const [sSubmissionStatus, setSubmissionStatus] = useState(null);
	const history = useHistory();
	return (
		<>
			{jobCategoryMapping && (
				<Formik
					initialValues={{
						...jobCategoryMapping?.extractValues(),
					}}
					validationSchema={Yup.object().shape({
						[JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT]: Yup.object()
							.shape({
								id: Yup.number(),
								label: Yup.string(),
							})
							.nullable()
							.required('Department is required.'),
						[JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE]: Yup.object()
							.shape({
								id: Yup.number(),
								label: Yup.string(),
							})
							.nullable()
							.required('Job Title is required'),
						[JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_ID]:
							Yup.object()
								.shape({
									id: Yup.number(),
									label: Yup.string(),
								})
								.nullable()
								.required('Job Category ID is required'),
					})}
					validateOnMount
					onSubmit={async (values, actions) => {
						actions.setSubmitting(true);
						await submitJobCategoryMappingForm(
							{
								...new JobCategoryMappingDTO(
									values,
									isNew
								).prepare(maintenanceInformation, isNew),
							},
							isNew
						)
							.then(response => {
								actions.setSubmitting(false);
								if (response.data.payload === false) {
									let displayMessage =
										'Submitted Unsuccessfully';
									if (
										response?.data?.messages &&
										response.data.messages.length > 0 &&
										response.data.messages[0].Value
									) {
										displayMessage =
											response.data.messages[0].Value;
									}
									setSubmissionStatus({
										error: true,
										submitted: false,
										message: displayMessage,
									});
								} else {
									setSubmissionStatus({
										error: false,
										submitted: true,
										message: 'Submitted Successfully',
									});
								}
							})
							.catch(() => {
								actions.setSubmitting(false);
								setSubmissionStatus({
									error: true,
									submitted: false,
									message: 'Submission was unsuccessful',
								});
							});
						actions.setSubmitting(true);
					}}
				>
					{formik => (
						<Form>
							{sSubmissionStatus?.message && (
								<Alert
									severity={
										sSubmissionStatus?.error
											? 'error'
											: 'success'
									}
									variant='filled'
									onClose={() => {
										setSubmissionStatus(
											prev => (prev.message = '')
										);
									}}
								>
									{sSubmissionStatus?.message}
								</Alert>
							)}
							<Stack
								sx={{ marginTop: '32px' }}
								direction='column'
								spacing={{
									xs: 0.5,
									sm: 0.5,
									md: 1,
								}}
							>
								<FormControlCustomized>
									<HRDepartmentAsyncAutocomplete
										name={
											JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT
										}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_MAPPING_KEYS.DAY_IN_THE_LIFE_ACTIVITY_POST}`}
										label={
											<>
												<RequiredIndicator />
												Department
											</>
										}
										onchange={value => {
											formik.setFieldValue(
												JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT,
												value
											);
										}}
										onBlur={() =>
											formik.setFieldTouched(
												JOB_CATEGORY_MAPPING_KEYS.DEPARTMENT,
												true
											)
										}
										value={
											formik.values[
												JOB_CATEGORY_MAPPING_KEYS
													.DEPARTMENT
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_MAPPING_KEYS
													.DEPARTMENT
											] &&
											!!formik.errors[
												JOB_CATEGORY_MAPPING_KEYS
													.DEPARTMENT
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_MAPPING_KEYS
													.DEPARTMENT
											] &&
											formik.errors[
												JOB_CATEGORY_MAPPING_KEYS
													.DEPARTMENT
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<HRJobTitleAsyncAutocomplete
										name={
											JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE
										}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE}`}
										label={
											<>
												<RequiredIndicator />
												Job Title
											</>
										}
										onchange={value => {
											formik.setFieldValue(
												JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE,
												value
											);
										}}
										onBlur={() =>
											formik.setFieldTouched(
												JOB_CATEGORY_MAPPING_KEYS.JOB_TITLE,
												true
											)
										}
										value={
											formik.values[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_TITLE
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_TITLE
											] &&
											!!formik.errors[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_TITLE
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_TITLE
											] &&
											formik.errors[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_TITLE
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<FormControlCustomized>
									<JobCategoryAsyncAutocomplete
										name={
											JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_ID
										}
										id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_TEXT_FIELD}--${JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_ID}`}
										label={
											<>
												<RequiredIndicator />
												Job Category
											</>
										}
										onchange={value => {
											formik.setFieldValue(
												JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_ID,
												value
											);
										}}
										onBlur={() =>
											formik.setFieldTouched(
												JOB_CATEGORY_MAPPING_KEYS.JOB_CATEGORY_ID,
												true
											)
										}
										value={
											formik.values[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_CATEGORY_ID
											]
										}
										error={
											!!formik.touched[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_CATEGORY_ID
											] &&
											!!formik.errors[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_CATEGORY_ID
											]
										}
										helperText={
											formik.touched[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_CATEGORY_ID
											] &&
											formik.errors[
												JOB_CATEGORY_MAPPING_KEYS
													.JOB_CATEGORY_ID
											]
										}
										disabled={sSubmissionStatus?.submitted}
									/>
								</FormControlCustomized>
								<Button
									id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--new`}
									type='submit'
									variant={'contained'}
									disabled={
										(!!formik.errors &&
											Object.keys(formik.errors).length >
												0) ||
										!formik.dirty ||
										formik.isSubmitting ||
										sSubmissionStatus?.submitted
									}
								>
									{formik.isSubmitting
										? 'Submitting...'
										: sSubmissionStatus?.submitted
										? 'Submitted'
										: isNew
										? 'Create'
										: 'Save'}
								</Button>
								{sSubmissionStatus?.submitted && (
									<>
										{isNew && (
											<Button
												id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--new`}
												variant={'contained'}
												color={'success'}
												onClick={() => {
													formik.resetForm({
														...jobCategoryMapping.extractValues(),
													});
													setSubmissionStatus(null);
												}}
											>
												Submit New Job Category Mapping
											</Button>
										)}
										<Button
											id={`${BEM_NAME_ELEMENTS.VIEW_JOB_CATEGORY_MAPPING}__${BEM_NAME_ELEMENTS.FIELD_BUTTON}--home`}
											variant={'contained'}
											color={'primary'}
											onClick={() =>
												history.push(
													'/EpicUtilities/Home'
												)
											}
										>
											Back to Epic Administration Home
										</Button>
									</>
								)}
							</Stack>
						</Form>
					)}
				</Formik>
			)}
		</>
	);
};
export default FormView;
